import * as t from 'io-ts';
import {isArray, isEqual} from 'lodash';

import {Atom, ClientAtom, PreAtom, serializeAtom} from './expressionAtomModel';
import {ExpressionRef} from './expressionRefModel';
import {InferredType, ResultTypesEnum} from './infer/expressionResultModel';
import {RExpressionId} from './iotsCodecs';

/*
 * Types.
 */

export type ExpressionId = t.TypeOf<typeof RExpressionId>;

export function brandExpressionId(id: string): ExpressionId {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return id as ExpressionId;
}

export const PREVIOUS_RESULT_ID = brandExpressionId('previous_result');

export interface PreExpressionItem {
  id: string;
  name: string;
  tooltip?: string;
  atoms: ReadonlyArray<ClientAtom>;
  inferredType: InferredType;
}

export interface PreExpression {
  id: ExpressionId;
  name: string;
  atoms: ReadonlyArray<PreAtom>;
}

export interface Expression {
  id: ExpressionId;
  name: string;
  defaultName: string;
  atoms: ReadonlyArray<ClientAtom>;
  inferredType: InferredType;
  dryRun?: ExpressionDryRunSnapshot;
}

export const Expression = {
  equals(a: Expression, b: Expression): boolean {
    return a.name === b.name && areAtomsEqual(a.atoms, b.atoms);
  },
};

export interface ExpressionDryRunSnapshot {
  type: ResultTypesEnum;
  value: string;
}

/*
 * Type guards.
 */

export function isExpression(expression: any): expression is Expression {
  return expression && isArray(expression.atoms);
}

/*
 * Helpers.
 */

export function buildExpressionName(expression: Expression) {
  return expression.name || expression.defaultName;
}

export function serializeExpression(expression: Expression): any {
  return {
    id: expression.id,
    name: buildExpressionName(expression),
    atoms: expression.atoms.map(serializeAtom),
  };
}

function areAtomsEqual(a: ReadonlyArray<Atom>, b: ReadonlyArray<Atom>) {
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    const atomA = a[i];
    const atomB = b[i];

    if (atomA === atomB) {
      continue;
    }

    if (atomA.transform !== atomB.transform) {
      return false;
    }

    if (!isEqual(atomA.params, atomB.params)) {
      return false;
    }
  }

  return true;
}

export function isPreviousResultRef(ref: ExpressionRef) {
  return ref.expression === PREVIOUS_RESULT_ID;
}
